import { motion } from "framer-motion";
import { PRODUCT_IMAGES } from "../../lib/constants";
import { Heart, Sparkles, Award, Clock } from "lucide-react";

const StorySection = () => {
  const values = [
    {
      icon: Heart,
      title: "Hecho con Amor",
      description: "Cada brownie lleva un pedacito de nuestro corazón",
    },
    {
      icon: Sparkles,
      title: "Ingredientes Premium",
      description: "Solo lo mejor para crear momentos inolvidables",
    },
    {
      icon: Award,
      title: "Presentación Impecable",
      description: "Porque un regalo también entra por los ojos",
    },
    {
      icon: Clock,
      title: "Puntuales Siempre",
      description: "Tu sorpresa llega justo cuando debe llegar",
    },
  ];

  return (
    <section
      id="historia"
      className="py-20 md:py-32 bg-[#FDFBF7] overflow-hidden"
      data-testid="story-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              <img
                src={PRODUCT_IMAGES.browniesWithBow}
                alt="Urban Brownies Historia"
                className="w-full rounded-3xl shadow-2xl"
              />
              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -right-6 bg-[#C88037] text-white p-6 rounded-2xl shadow-xl"
              >
                <div className="text-4xl font-serif font-bold">1</div>
                <div className="text-sm">Año de amor</div>
              </motion.div>
            </div>
            {/* Background decoration */}
            <div className="absolute -z-10 -top-8 -left-8 w-full h-full bg-[#F4EDE4] rounded-3xl" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-[#C88037] font-medium text-sm uppercase tracking-wider"
              >
                Nuestra Historia
              </motion.span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 leading-tight">
                Un refugio que se convirtió en{" "}
                <span className="italic text-[#C88037]">tu momento dulce</span>
              </h2>
            </div>

            <div className="space-y-4 text-[#5E4B3C] leading-relaxed">
              <p>
                <strong className="text-[#2D1810]">Urban Brownies nació hace un año,</strong> en medio de la incertidumbre, 
                como un refugio personal que se convirtió en el punto de partida de un sueño.
              </p>
              <p>
                Todo comenzó con un antojo simple: preparar brownies para la familia y amigos cercanos. 
                Lo que no imaginamos fue que cada mordisco arrancaría sonrisas, y cada sonrisa nos motivaría a dar más.
              </p>
              <p className="text-[#2D1810] font-medium italic border-l-4 border-[#C88037] pl-4">
                "No vendemos brownies. Creamos recuerdos dulces que se disfrutan con el corazón."
              </p>
            </div>

            {/* Values Grid */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#F4EDE4] p-4 rounded-xl group hover:bg-[#C88037] transition-colors duration-300"
                >
                  <value.icon
                    className="w-8 h-8 text-[#C88037] group-hover:text-white transition-colors mb-2"
                    strokeWidth={1.5}
                  />
                  <h4 className="font-semibold text-[#2D1810] group-hover:text-white transition-colors text-sm">
                    {value.title}
                  </h4>
                  <p className="text-[#5E4B3C] group-hover:text-white/80 transition-colors text-xs mt-1">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
